/**
 * Resourcing Permission Hook
 * 检查用户是否有权限访问 Resourcing 页面
 * 
 * 只有 system-manager 组成员或 platform-admin 角色可以访问
 */

import { useState, useEffect } from 'react';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';

export const useResourcingPermission = () => {
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const checkPermission = async () => {
      try {
        // 检查 permission 系统是否启用
        const permissionEnabled = configApi.getOptionalBoolean('permission.enabled') ?? false;
        
        // 如果 permission 系统未启用，允许所有用户访问
        if (!permissionEnabled) {
          setHasPermission(true);
          setLoading(false);
          return;
        }

        const identity = await identityApi.getBackstageIdentity();
        const userOwnershipRefs = identity.ownershipEntityRefs || [];
        
        // 检查用户是否属于允许的组
        // - system-manager 组
        // - argocd-admin 组（platform-admin 角色）
        const allowedGroups = [
          'group:default/system-manager',
          'group:default/argocd-admin',
        ];
        
        const hasAllowedGroup = userOwnershipRefs.some(ref => 
          allowedGroups.some(allowed => 
            ref === allowed || ref.toLowerCase() === allowed.toLowerCase()
          )
        );
        
        // 检查是否是特殊用户（backstage、test456）
        const userRef = identity.userEntityRef;
        const specialUsers = [
          'user:default/backstage',
          'user:default/test456',
        ];
        const isSpecialUser = specialUsers.some(special => 
          userRef === special || userRef?.toLowerCase() === special.toLowerCase()
        );
        
        setHasPermission(hasAllowedGroup || isSpecialUser);
      } catch {
        // 检查失败时默认不允许（安全起见）
        setHasPermission(false);
      } finally {
        setLoading(false);
      }
    };

    checkPermission();
  }, [identityApi, configApi]);

  return { hasPermission, loading };
};

